import {
  c2x2Det,
  c2x2Exp,
  c2x2FromReal,
  c2x2Mul,
  c2x2Trace,
  cmatIsInvertible,
  toTuples,
} from "@csk/core/linear";

export interface DiagonalPair {
  readonly a: number;
  readonly d: number;
}

export interface DiagonalTorusState {
  readonly element: DiagonalPair;
  readonly product: DiagonalPair;
  readonly inverse: DiagonalPair | null;
  readonly determinant: number;
  readonly trace: number;
  readonly expTrace: number;
  readonly invertible: boolean;
}

export interface AxiomDerivation {
  readonly code: "A1" | "A2" | "A3" | "A4";
  readonly title: string;
  readonly explanation: string;
  readonly status: string;
  readonly tone: "success" | "warning";
  readonly derivation?: string;
}

export function evaluateDiagonalTorus(a: number, d: number): DiagonalTorusState {
  const matrix = c2x2FromReal([
    [a, 0],
    [0, d],
  ]);
  const product = c2x2Mul(matrix, matrix);
  const expMatrix = c2x2Exp(matrix);
  const determinant = c2x2Det(matrix)[0];
  const trace = c2x2Trace(matrix)[0];
  const productTuples = toTuples(product);
  return {
    element: { a, d },
    product: {
      a: productTuples[0]?.[0]?.[0] ?? Number.NaN,
      d: productTuples[1]?.[1]?.[0] ?? Number.NaN,
    },
    inverse: cmatIsInvertible(matrix) ? { a: 1 / a, d: 1 / d } : null,
    determinant,
    trace,
    expTrace: c2x2Det(expMatrix)[0],
    invertible: cmatIsInvertible(matrix),
  };
}

export function axiomDerivations(state: DiagonalTorusState): readonly AxiomDerivation[] {
  return [
    {
      code: "A1",
      title: "Authority subgroup",
      explanation: "YOUR CHOICE — diag(a,d) with a,d ≠ 0",
      status: "governance decision",
      tone: "warning",
    },
    {
      code: "A2",
      title: "Composition preserves structure",
      explanation:
        "FREE — tr additive, det multiplicative, exp semigroup, group closure",
      status: "automatic",
      tone: "success",
      derivation: "ring axioms of ℂ",
    },
    {
      code: "A3",
      title: "Temporal commitment",
      explanation: `FREE — exp(tr) = ${state.expTrace.toFixed(2)}, det = ${state.determinant.toFixed(2)}, exp never zero`,
      status: "automatic",
      tone: "success",
      derivation: "Jacobi: det(exp X) = exp(tr X)",
    },
    {
      code: "A4",
      title: "Resource conservation",
      explanation: `FREE — tr(${state.element.a.toFixed(1)}+${state.element.d.toFixed(1)}) = ${state.trace.toFixed(1)}, det = ${state.determinant.toFixed(1)}`,
      status: "automatic",
      tone: "success",
      derivation: "A2 + A3 combined",
    },
  ];
}

export function formatDiagonal(pair: DiagonalPair | null, precision = 1): readonly string[] {
  if (pair === null) return ["NaN", "NaN"];
  return [pair.a.toFixed(precision), pair.d.toFixed(precision)];
}
